import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../Context/TenantContext';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import { useSubscription } from '../Context/SubscriptionContext';
import { hasFullAccess } from '../utils/paymentStatus';
import useSkillGenieSession from '../hooks/useSkillGenieSession';
import { supportedLanguages } from '../utils/languageSupport';
import { ArrowLeft, Clock, LayoutGrid, Home, GraduationCap, Target, ChevronDown } from "lucide-react";
import { SparklesIcon } from "@heroicons/react/24/outline";

import { updateLearningConsistency } from '../utils/consistency';
import { UserContext } from './UserContext';

const SkillgenLaunchpadQuestionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { technology } = useParams();
  const { user: userContext } = useContext(UserContext);
  const userIdFromContext = userContext?.uid || userContext?.id || localStorage.getItem('userId');

  const locationData = location.state || {};
  const localData = JSON.parse(localStorage.getItem('pendingLevelComplete')) || {};

  const track = locationData.track ?? localData.track ?? '';
  const topic = locationData.topic ?? localData.topic ?? '';
  const level = locationData.level ?? localData.completedLevel ?? '';
  const fullTrackName = locationData.fullTrackName ?? localData.fullTrackName ?? '';

  const requiresPremium = parseInt(level, 10) >= 6;

  const { tenant } = useTenant();

  const user = JSON.parse(localStorage.getItem('user'));
  const userId = user?.userId || 'UnknownUser';

 // Get language preference from session or location state
 const { session } = useSkillGenieSession();
 const [mcqLanguage, setMcqLanguage] = useState("en");
 const language = locationData?.language || session?.language || 'en';

 const [questions, setQuestions] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');

  useEffect(() => {
    if (userIdFromContext && technology && level) {
      axios.post(`${API_BASE_URL}/api/user/attempts`, {
        user_id: userIdFromContext,
        technology,
        module: 'Launchpad',
        level: parseInt(level),
        score: 0,
        status: 'Started'
    }, { headers: { 'user-id': userIdFromContext } }).catch(err => console.error('Failed to report attempt:', err));

      updateLearningConsistency(userIdFromContext, 'MCQ_START', 'Launchpad', technology);
    }
  }, [userIdFromContext, technology, level]);
 const [currentIndex, setCurrentIndex] = useState(0);
 const [answers, setAnswers] = useState({});
 const [elapsedSeconds, setElapsedSeconds] = useState(0);

 const [mode, setMode] = useState(location.state?.mode || '');
 const [showModeModal, setShowModeModal] = useState(!location.state?.mode);
 const [explanation, setExplanation] = useState('');
 const [explaining, setExplaining] = useState(false);
 const [showExplanation, setShowExplanation] = useState(false);
 const [explanationError, setExplanationError] = useState('');

 const formatTime = (seconds) => {
 const mins = String(Math.floor(seconds / 60)).padStart(2,"0");
 const secs = String(seconds % 60).padStart(2,"0");
 return `00:${mins}:${secs}`;
 };

 const formatExplanationText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter(Boolean).join("\n");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => {
        if (typeof item === "string") return `${key}: ${item}`;
        if (Array.isArray(item)) return `${key}: ${item.join(", ")}`;
        if (item && typeof item === "object") {
          return `${key}:\n${Object.entries(item).map(([subKey, subItem]) => `- ${subKey}: ${subItem}`).join("\n")}`;
        }
        return "";
      })
      .filter(Boolean)
      .join("\n\n");
  }
  return String(value);
 };

 const [hasAccess, setHasAccess] = useState(!requiresPremium);
 const [checkingAccess, setCheckingAccess] = useState(requiresPremium);

  useEffect(() => {
    let cancelled = false;
    const checkPremiumAccess = async () => {
      // BYPASS FOR TENANT
      if (tenant) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      if (!requiresPremium) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }
 
 if (!userId || userId ==="UnknownUser") {
 if (!cancelled) {
 setHasAccess(false);
 setCheckingAccess(false);
 alert("Please log in to access premium Launchpad MCQs.");
 navigate("/subscription-plans");
 setLoading(false);
 }
 return;
 }
 
 try {
 setCheckingAccess(true);
 console.log(`[Launchpad MCQs] Checking premium access for ${userId}, level: ${level}`);
 const allowed = await hasFullAccess(userId);
 console.log(`[Launchpad MCQs] Premium access result for ${userId}: ${allowed}`);
 if (!cancelled) {
 setHasAccess(allowed);
 setCheckingAccess(false);
 if (!allowed) {
 console.log(`[Launchpad MCQs] Access denied for ${userId} - blocking`);
 alert("Premium subscription required to unlock Advanced & Expert launchpad levels.");
 navigate("/subscription-plans");
 setLoading(false);
 setError("Premium subscription required.");
 } else {
 console.log(`[Launchpad MCQs] Access granted for ${userId}`);
 }
 }
 } catch (err) {
 console.error("[Launchpad MCQs] Premium check failed:", err);
 // If payment check fails, block access to be safe
 if (!cancelled) {
 setHasAccess(false);
 setCheckingAccess(false);
 alert("Unable to verify subscription. Please try again.");
 navigate("/subscription-plans");
 setLoading(false);
 }
 }
 };
 checkPremiumAccess();
 return () => { cancelled = true; };
 }, [userId, level, navigate, requiresPremium]);

 useEffect(() => {
 if (!hasAccess && requiresPremium) return; // Don't load if blocked
 
 if (!track || !level || !topic) {
 setError('Something went wrong. Missing topic or level information.');
 setLoading(false);
 return;
 }

 const isFullTrack = topic.toLowerCase().trim() === fullTrackName.toLowerCase().trim();
 const fetchURL = isFullTrack
 ? `${API_BASE_URL}/sgl/${track}/level/${level}`
 : `${API_BASE_URL}/sgl/${track}/subtopic/${encodeURIComponent(topic)}/level/${level}`;

 axios.get(fetchURL)
 .then((res) => {
 if (Array.isArray(res.data)) {
 setQuestions(res.data);
 } else {
 setError('Invalid data format from server.');
 }
 setLoading(false);
 })
 .catch((err) => {
 console.error('❌ Error fetching questions:', err);
 setError('Failed to load questions. Please try again later.');
 setLoading(false);
 });
 }, [track, topic, level, fullTrackName, hasAccess, requiresPremium]);

 useEffect(() => {
 const timer = setInterval(() => setElapsedSeconds((prev) => prev + 1), 1000);
 return () => clearInterval(timer);
 }, []);

 const handleOptionChange = (option) => {
 setAnswers((prev) => ({ ...prev, [currentIndex]: option }));
 };

  useEffect(() => {
    if (mode === 'learning' && answers[currentIndex]) {
      fetchExplanation();
    }
  }, [currentIndex, mode, answers[currentIndex]]);

  const fetchExplanation = async () => {
    if (mode !== 'learning') return;

    setExplaining(true);
    setExplanationError("");
    setExplanation(""); // Clear previous explanation
    setShowExplanation(false);

    try {
      const userObj = JSON.parse(localStorage.getItem("user") || "{}");
      const userIdVal = userObj?.userId || userObj?.user_id || localStorage.getItem("userId") || userId;

      const res = await axios.post(`${API_BASE_URL}/api/explain`, {
        userId: userIdVal,
        explainType: 'mcq',
        context: {
          question: questions[currentIndex]?.question,
          options: {
            A: questions[currentIndex]?.option_a,
            B: questions[currentIndex]?.option_b,
            C: questions[currentIndex]?.option_c,
            D: questions[currentIndex]?.option_d,
          },
          correct_option: questions[currentIndex]?.correct_option?.toUpperCase(),
          language: mcqLanguage,
          technology: technology || "SkillGenie",
          level: level || "General"
        }
      }, {
        headers: { 'user-id': userIdVal }
      });

      if (res.data && res.data.ok) {
        setExplanation(formatExplanationText(res.data.explanation || res.data.data?.explanation) || "No explanation provided.");
        setShowExplanation(true);
      } else {
        setExplanationError(res.data?.error || "Failed to fetch explanation.");
      }
    } catch (err) {
      console.error("Error fetching explanation:", err);
      setExplanationError("Failed to generate explanation. Please try again.");
    } finally {
      setExplaining(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (mode === 'learning') {
      alert('Learning session completed! You can now return to the launchpad.');
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role === "student" && user.tenant) {
        navigate("/student/launchpadpage");
      } else {
        navigate("/launchpadpage");
      }
      return;
    }

    const idBasedAnswers = {};
    questions.forEach((q, idx) => {
      if (answers[idx]) idBasedAnswers[q.id] = answers[idx];
    });

    if (Object.keys(idBasedAnswers).length === 0) {
      alert('Please answer at least one question.');
      return;
    }

 const timeFormatted = formatTime(elapsedSeconds);
 const isFullTrack = topic.toLowerCase().trim() === fullTrackName.toLowerCase().trim();

 try {
      const res = await axios.post(`${API_BASE_URL}/evaluate-sgl`, {
        answers: idBasedAnswers,
        track,
        subtopic: isFullTrack ? fullTrackName : topic,
        level,
        user_id: userId,
        time_taken: timeFormatted,
        total_questions: questions.length, // ✅ Send total questions count
      });

      const { correct, wrong, score } = res.data;

      if (userIdFromContext) {
        axios.post(`${API_BASE_URL}/api/user/attempts`, {
          user_id: userIdFromContext,
          technology,
          module: 'Launchpad',
          level: parseInt(level),
          questions_attempted: questions.length,
          questions_correct: correct,
          score: score,
          status: 'Completed'
        }, { headers: { 'user-id': userIdFromContext } }).catch(err => console.error('Failed to report final attempt:', err));

        updateLearningConsistency(userIdFromContext, 'MCQ_SUBMIT', 'Launchpad', technology, { level, score });
      }

 if (score >= 80) {
 localStorage.setItem('levelCompleted', 'true');
 localStorage.setItem('pendingLevelComplete', JSON.stringify({
 topic,
 completedLevel: parseInt(level),
 track,
 fullTrackName,
 }));
 }

 navigate('/skillgenlaunchpadclearance', {
 state: {
 correct,
 wrong,
 score,
 time: timeFormatted,
 topic,
 level,
 track,
 fullTrackName,
 answers: idBasedAnswers,
 total_questions: questions.length, // ✅ Pass total questions
 }
 });

 } catch (err) {
 console.error('❌ Submit error:', err);
 alert('Submission failed. Please try again.');
 }
 };

 if (checkingAccess && requiresPremium) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
 <p className="text-primary font-bold">Verifying premium access...</p>
 </div>
 </div>
 );
 }

 if (requiresPremium && !hasAccess) {
 return (
 <div className="min-h-screen bg-bgMain text-body flex items-center justify-center px-4">
 <div className="text-center max-w-md">
 <div className="text-6xl mb-6">🔒</div>
 <h2 className="text-3xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">Premium Access Required</h2>
 <p className="text-lg text-muted mb-8 leading-relaxed">
 Advanced SkillGenie levels (6+) require a premium subscription to access. Unlock your full potential today!
 </p>
 <button
 onClick={() => navigate("/skillgenpayments")}
 className="btn-primary-gradient w-full text-xl py-4 shadow-xl"
 >
 Upgrade to Premium
 </button>
 <button
 onClick={() => navigate(-1)}
 className="block mx-auto mt-6 text-muted hover:text-primary text-sm transition font-bold"
 >
 ← Go Back
 </button>
 </div>
 </div>
 );
 }

 if (loading) return <div className="min-h-screen bg-bgMain text-primary flex items-center justify-center font-bold">Loading questions...</div>;
 if (error) return <div className="min-h-screen bg-bgMain text-primary flex items-center justify-center font-bold">{error}</div>;
 if (!questions.length) return <div className="min-h-screen bg-bgMain text-muted flex items-center justify-center font-bold">No questions found.</div>;

 if (showModeModal) {
 return (
 <div className="absolute inset-0 bg-bgMain/90 backdrop-blur-md flex items-center justify-center z-50">
 <div className="bg-bgSection p-10 rounded-3xl border border-primary/30 shadow-2xl text-center max-w-md w-full relative overflow-hidden">
 <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 rounded-full blur-3xl"></div>
 <h2 className="text-3xl font-extrabold text-primary mb-8">Select Mode</h2>
 <div className="flex flex-col gap-4">
 <button
 onClick={() => { setMode('learning'); setShowModeModal(false); }}
 className="btn-primary-gradient w-full py-4 text-lg"
 >
 Learning Mode
 </button>
 <button
 onClick={() => { setMode('practice'); setShowModeModal(false); }}
 className="w-full py-4 border-2 border-secondary text-primary hover:bg-secondary hover:text-white rounded-full font-bold transition-all text-lg"
 >
 Practice Mode
 </button>
 </div>
 </div>
 </div>
 );
 }

 const selectedOption = answers[currentIndex];
 const currentQuestion = questions[currentIndex];
 const correctOption = currentQuestion?.correct_option?.toUpperCase();
 const correctAnswerText = correctOption ? currentQuestion?.[`option_${correctOption.toLowerCase()}`] : "";

 const goHome = () => {
    navigate("/dashboard");
  };

  return (
    <div className="flex bg-[#F8FAFC] text-body min-h-screen relative flex-row">
      {/* Sidebar - Fixed Royal Purple */}
      <div className="w-64 bg-[#5B21B6] p-5 flex flex-col h-screen text-white shadow-2xl z-[80] fixed left-0 top-0">
        <div className="mb-10 mt-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg bg-white/20">
              <SparklesIcon className="text-white h-6 w-6" />
            </div>
            <span className="text-xl font-black tracking-tight text-white uppercase">
              The Skill Genie
            </span>
          </div>

          <div className="text-[9px] font-black uppercase text-white/50 mb-1 tracking-widest">Launchpad Track</div>
          <div className="text-base font-bold text-white capitalize truncate">{track}</div>
          <div className="text-[9px] font-black uppercase text-white/50 mt-4 mb-1 tracking-widest">Topic</div>
          <div className="text-sm font-bold text-white capitalize leading-tight">{topic}</div>
          <div className="text-xs font-medium text-white/70 mt-1">Level {level}</div>
        </div>
        
        <div className="mb-10">
          <div className="text-[9px] font-black uppercase text-white/50 mb-2 tracking-widest">Timer</div>
          <div className="text-3xl font-black text-white font-mono">
            {formatTime(elapsedSeconds)}
          </div>
        </div>

        <div className="text-[9px] font-black uppercase text-white/50 mb-3 tracking-widest">Questions</div>
        <div className="grid grid-cols-5 gap-2 text-xs overflow-y-auto custom-scrollbar pr-1 flex-1">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setCurrentIndex(idx);
                setShowExplanation(false);
                setExplanation("");
              }}
              className={`w-9 h-9 rounded-xl font-bold transition-all ${
                idx === currentIndex
                  ? "bg-white text-[#5B21B6] shadow-xl scale-110"
                  : answers[idx]
                  ? "bg-white/30 text-white border border-white/20"
                  : "bg-white/5 text-white/40 border border-white/10 hover:border-white/30"
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <button
            onClick={goHome}
            className="w-full px-4 py-3 rounded-xl text-sm font-bold transition-all bg-white/10 hover:bg-white/20 text-white flex items-center justify-center gap-2"
          >
            <Home size={18} /> Exit to Home
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="flex-1 ml-64 p-8 flex flex-col items-center bg-[#F8FAFC] min-h-screen overflow-y-auto">
        {showModeModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100]">
            <div className="bg-white p-10 rounded-[2.5rem] text-center shadow-2xl max-w-sm w-full animate-in zoom-in duration-300">
              <h2 className="text-2xl font-black text-gray-900 mb-8 uppercase tracking-tight">Select Mode</h2>
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => {
                    setMode("learning");
                    setShowModeModal(false);
                  }}
                  className="w-full py-4 rounded-2xl bg-[#5B21B6] text-white font-bold hover:bg-[#4C1D95] transition-all shadow-lg shadow-purple-200 flex items-center justify-center gap-3"
                >
                  <GraduationCap size={20} /> Learning Mode
                </button>
                <button
                  onClick={() => {
                    setMode("practice");
                    setShowModeModal(false);
                  }}
                  className="w-full py-4 rounded-2xl bg-white border-2 border-[#5B21B6] text-[#5B21B6] font-bold hover:bg-purple-50 transition-all flex items-center justify-center gap-3"
                >
                  <Target size={20} /> Practice Mode
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="w-full max-w-4xl pt-4 pb-20">
            <div className="flex justify-between items-center mb-8">
               <button
                  onClick={() => {
                    const user = JSON.parse(localStorage.getItem("user"));
                    if (user && user.role === "student" && user.tenant) {
                      navigate("/student/launchpadpage");
                    } else {
                      navigate("/launchpadpage");
                    }
                  }}
                  className="px-5 py-2 bg-[#5B21B6] text-white font-bold rounded-xl hover:bg-[#4C1D95] transition shadow-lg shadow-purple-200 flex items-center gap-2 text-sm"
                >
                  <ArrowLeft size={16} /> Back to Launchpad
                </button>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border-2 border-purple-100 relative overflow-hidden mb-8">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#5B21B6]"></div>
              
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[9px] font-black uppercase text-[#5B21B6] tracking-[0.2em] bg-purple-50 px-3 py-1 rounded-full border border-purple-100">Level {level} • Question {currentIndex + 1} of {questions.length}</span>
                  <span className="text-[9px] font-black uppercase text-purple-300 tracking-widest">{Math.round(((currentIndex + 1) / questions.length) * 100)}% Complete</span>
                </div>
                
                {/* 📊 Progress Bar */}
                <div className="w-full h-1.5 bg-gray-100 rounded-full mb-6 overflow-hidden">
                  <div 
                    className="h-full bg-[#5B21B6] transition-all duration-500 ease-out"
                    style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                  ></div>
                </div>

                <h2 className="text-2xl font-black text-gray-900 leading-tight">
                  {questions[currentIndex]?.question}
                </h2>
              </div>

              <div className="space-y-3 mb-10">
                {["A", "B", "C", "D"].map((optKey) => {
                  const isSelected = selectedOption === optKey;
                  const isCorrect = questions[currentIndex]?.correct_option?.toUpperCase() === optKey;
                  const showResult = mode === "learning" && selectedOption;

                  return (
                    <label
                      key={optKey}
                      className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${
                        isSelected
                          ? showResult
                            ? isCorrect
                              ? "bg-green-50 border-green-500 shadow-lg shadow-green-100"
                              : "bg-red-50 border-red-500 shadow-lg shadow-red-100"
                            : "bg-purple-50 border-[#5B21B6] shadow-lg shadow-purple-100"
                          : showResult && isCorrect
                          ? "bg-green-50 border-green-500 shadow-lg shadow-green-100"
                          : "bg-gray-50 border-gray-100 hover:border-purple-200 hover:bg-white"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black transition-all text-sm ${
                        isSelected 
                          ? showResult
                            ? "bg-white " + (isCorrect ? "text-green-600" : "text-red-600")
                            : "bg-[#5B21B6] text-white" 
                          : showResult && isCorrect
                          ? "bg-white text-green-600"
                          : "bg-white text-gray-400 border border-gray-100"
                      }`}>
                        {optKey}
                      </div>
                      <input
                        type="radio"
                        name={`question-${currentIndex}`}
                        value={optKey}
                        checked={isSelected}
                        onChange={() => handleOptionChange(optKey)}
                        className="hidden"
                      />
                      <span className={`text-lg ${isSelected ? "text-gray-900 font-black" : "text-gray-700 font-bold"}`}>
                        {questions[currentIndex][`option_${optKey.toLowerCase()}`]}
                      </span>
                      {showResult && isSelected && (
                        <span className="ml-auto">
                          {isCorrect ? (
                            <span className="text-green-600 font-black text-[10px] uppercase">Correct!</span>
                          ) : (
                            <span className="text-red-600 font-black text-[10px] uppercase">Incorrect</span>
                          )}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-4 justify-between items-center pt-8 border-t border-gray-100">
                <button
                  onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentIndex === 0}
                  className="px-6 py-3 text-gray-400 font-black uppercase text-[10px] tracking-widest hover:text-[#5B21B6] disabled:opacity-30 transition flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Previous
                </button>

                <div className="flex items-center gap-4">
                  {mode === "learning" && (
                    <div className="flex items-center gap-3">
                      <select
                        value={mcqLanguage}
                        onChange={(e) => setMcqLanguage(e.target.value)}
                        className="bg-gray-50 text-gray-600 border border-gray-200 rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:border-[#5B21B6] transition-all"
                      >
                        {supportedLanguages.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={fetchExplanation}
                        className="px-6 py-3 bg-purple-50 text-[#5B21B6] rounded-xl font-bold uppercase text-[10px] tracking-widest hover:bg-[#5B21B6] hover:text-white transition-all border border-purple-100"
                        disabled={explaining}
                      >
                        {explaining ? "AI Thinking..." : "🧠 Re-explain"}
                      </button>
                    </div>
                  )}

                  {currentIndex === questions.length - 1 ? (
                    <button
                      onClick={handleFinalSubmit}
                      className="px-10 py-4 bg-[#5B21B6] text-white rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-purple-200 hover:bg-[#4C1D95] transition-all"
                    >
                      Final Submit
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setCurrentIndex((prev) => prev + 1);
                        setShowExplanation(false);
                        setExplanation("");
                      }}
                      className={`px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all shadow-xl ${
                        selectedOption
                          ? "bg-[#5B21B6] text-white shadow-purple-200 hover:bg-[#4C1D95]"
                          : "bg-gray-100 text-gray-300 cursor-not-allowed"
                      }`}
                      disabled={!selectedOption}
                    >
                      Next Question
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* AI Explanation Block */}
            {mode === "learning" && selectedOption && (
              <div className="bg-white p-6 rounded-[2rem] border-2 border-green-100 shadow-sm mb-6">
                <div className="text-[10px] font-black uppercase tracking-widest text-green-700 mb-2">Correct Answer</div>
                <div className="text-gray-900 font-black">
                  {correctOption}. {correctAnswerText}
                </div>
              </div>
            )}

            {explanationError && (
              <div className="bg-red-50 p-4 rounded-2xl border border-red-100 text-red-700 font-bold text-sm mb-6">
                {explanationError}
              </div>
            )}

            {showExplanation && explanation && (
              <div className="bg-white p-10 rounded-[2.5rem] shadow-xl border-2 border-purple-100 animate-in slide-in-from-bottom duration-500 relative overflow-hidden">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-2xl">
                    🤖
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-black text-xl">AI Assistant</h3>
                    <p className="text-[#5B21B6] text-[10px] font-black uppercase tracking-widest">
                      Contextual Explanation
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                  <p className="text-gray-700 font-medium leading-relaxed whitespace-pre-line text-lg">
                    {explanation}
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default SkillgenLaunchpadQuestionPage;


